import { Injectable, DestroyRef, inject, effect } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, merge } from 'rxjs';
import { AuthSessionService } from 'core/services/auth-session.service';
import { PlatformSettingsService } from 'core/services/platform-settings.service';

@Injectable({ providedIn: 'root' })
export class SessionIdleService {
  private readonly auth = inject(AuthSessionService);
  private readonly settings = inject(PlatformSettingsService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private timerId: ReturnType<typeof setTimeout> | null = null;
  private started = false;

  constructor() {
    effect(() => {
      const authenticated = this.auth.isAuthenticated();
      const minutes = this.settings.current().sessionIdleMinutes;
      if (!authenticated || minutes <= 0) {
        this.clearTimer();
        return;
      }
      this.ensureListeners();
      this.resetTimer();
    });
  }

  private ensureListeners(): void {
    if (this.started || typeof window === 'undefined') {
      return;
    }
    this.started = true;

    merge(
      fromEvent(window, 'mousemove'),
      fromEvent(window, 'mousedown'),
      fromEvent(window, 'keydown'),
      fromEvent(window, 'touchstart'),
      fromEvent(window, 'scroll'),
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.resetTimer());
  }

  private resetTimer(): void {
    this.clearTimer();
    if (!this.auth.isAuthenticated()) {
      return;
    }

    const minutes = this.settings.current().sessionIdleMinutes;
    if (minutes <= 0) {
      return;
    }

    this.timerId = setTimeout(() => this.expireSession(), minutes * 60 * 1000);
  }

  private clearTimer(): void {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private expireSession(): void {
    this.clearTimer();
    this.auth.clearLocalSession();
    void this.router.navigateByUrl('/session-expired');
  }
}
